import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { lastNDates, formatTime12h } from '../lib/dates.js';

// Last 30 days of briefings + checkouts. Three filters as toggles plus a
// separate "Friction MITs" section that surfaces texts you've carried
// forward 3+ times in the window.
export default function History() {
  const [briefings, setBriefings] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    missedDevotional: false,
    incomplete: false,
    frictionMITs: false,
  });

  useEffect(() => {
    let mounted = true;
    const since = lastNDates(30).slice(-1)[0];

    Promise.all([
      supabase
        .from('daily_briefing')
        .select('*')
        .gte('date', since)
        .order('date', { ascending: false }),
      supabase
        .from('daily_checkout')
        .select('*')
        .gte('date', since)
        .order('date', { ascending: false }),
    ]).then(([{ data: b }, { data: c }]) => {
      if (!mounted) return;
      setBriefings(b ?? []);
      setCheckouts(c ?? []);
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Build combined rows for each date in the window (30 days).
  const rows = useMemo(() => {
    const bByDate = new Map(briefings.map((r) => [r.date, r]));
    const cByDate = new Map(checkouts.map((r) => [r.date, r]));
    return lastNDates(30).map((date) => {
      const b = bByDate.get(date) || null;
      const c = cByDate.get(date) || null;
      const mitsDone = [1, 2, 3].reduce(
        (n, i) => (c && c[`mit_${i}_done`] ? n + 1 : n),
        0
      );
      return {
        date,
        briefing: b,
        checkout: c,
        mitsDone,
      };
    });
  }, [briefings, checkouts]);

  // Compute friction MITs: texts that have mit_X_carry_forward=true in 3+ checkouts.
  const frictionMITs = useMemo(() => {
    const counts = new Map();
    for (const c of checkouts) {
      const b = briefings.find((b) => b.date === c.date);
      if (!b) continue;
      for (const i of [1, 2, 3]) {
        if (c[`mit_${i}_carry_forward`] && b[`mit_${i}_text`]) {
          const t = b[`mit_${i}_text`];
          counts.set(t, (counts.get(t) || 0) + 1);
        }
      }
    }
    return [...counts.entries()]
      .filter(([, n]) => n >= 3)
      .sort((a, b) => b[1] - a[1])
      .map(([text, count]) => ({ text, count }));
  }, [briefings, checkouts]);

  // Apply filters.
  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      if (filters.missedDevotional) {
        if (r.briefing?.devotional !== false && r.briefing?.devotional !== null) {
          return false;
        }
      }
      if (filters.incomplete) {
        const briefingDone = !!r.briefing?.completed_at;
        const checkoutDone = !!r.checkout?.completed_at;
        if (briefingDone && checkoutDone) return false;
      }
      return true;
    });
  }, [rows, filters]);

  if (loading) {
    return <div className="text-center text-text-muted py-12">Loading…</div>;
  }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-text-muted uppercase tracking-wider mr-2">
          Filter:
        </span>
        <FilterChip
          active={filters.missedDevotional}
          onClick={() =>
            setFilters((f) => ({ ...f, missedDevotional: !f.missedDevotional }))
          }
        >
          Missed devotional
        </FilterChip>
        <FilterChip
          active={filters.incomplete}
          onClick={() =>
            setFilters((f) => ({ ...f, incomplete: !f.incomplete }))
          }
        >
          Incomplete briefing / checkout
        </FilterChip>
        <FilterChip
          active={filters.frictionMITs}
          onClick={() =>
            setFilters((f) => ({ ...f, frictionMITs: !f.frictionMITs }))
          }
        >
          Friction MITs ({frictionMITs.length})
        </FilterChip>
      </div>

      {/* Friction MITs section (collapsible) */}
      {filters.frictionMITs && (
        <div className="bg-bg-card border border-border-base rounded p-4">
          <h3 className="text-xs uppercase tracking-wider text-text-secondary mb-3">
            🚨 MITs carried forward 3+ times
          </h3>
          {frictionMITs.length === 0 ? (
            <div className="text-sm text-text-muted">
              Nothing carried 3+ times in the last 30 days. Clean slate.
            </div>
          ) : (
            <ul className="space-y-2">
              {frictionMITs.map((m) => (
                <li
                  key={m.text}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-text-primary flex-1">{m.text}</span>
                  <span className="text-xs px-2 py-0.5 bg-accent-red/15 text-accent-red rounded shrink-0">
                    {m.count}×
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Main table */}
      <div className="bg-bg-card border border-border-base rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted bg-bg-input/40">
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium text-center">Devo</th>
              <th className="px-3 py-2 font-medium text-center">Workout</th>
              <th className="px-3 py-2 font-medium">Bed</th>
              <th className="px-3 py-2 font-medium text-center">MITs</th>
              <th className="px-3 py-2 font-medium text-center">Briefing</th>
              <th className="px-3 py-2 font-medium text-center">Checkout</th>
              <th className="px-3 py-2 font-medium">Win</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-6 text-center text-text-muted"
                >
                  No days match the active filters.
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => <Row key={r.date} row={r} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ row }) {
  const dateLabel = new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <tr className="border-t border-border-base/60 hover:bg-bg-input/20 transition">
      <td className="px-3 py-2 text-text-primary whitespace-nowrap">
        {dateLabel}
      </td>
      <td className="px-3 py-2 text-center">
        <Cell value={row.briefing?.devotional} />
      </td>
      <td className="px-3 py-2 text-center">
        <Cell value={row.briefing?.workout} />
      </td>
      <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
        {formatTime12h(row.briefing?.bed_time) || '—'}
      </td>
      <td className="px-3 py-2 text-center text-text-primary">
        {row.briefing ? `${row.mitsDone}/3` : '—'}
      </td>
      <td className="px-3 py-2 text-center">
        <RitualCell value={row.briefing?.completed_at} />
      </td>
      <td className="px-3 py-2 text-center">
        <RitualCell value={row.checkout?.completed_at} />
      </td>
      <td className="px-3 py-2 text-text-secondary truncate max-w-xs">
        {row.checkout?.daily_win || '—'}
      </td>
    </tr>
  );
}

function Cell({ value }) {
  if (value === true) return <span className="text-accent-green">✓</span>;
  if (value === false) return <span className="text-accent-red">✗</span>;
  return <span className="text-text-muted">—</span>;
}

function RitualCell({ value }) {
  if (value) return <span className="text-accent-green">✓</span>;
  return <span className="text-text-muted">—</span>;
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded border transition ${
        active
          ? 'bg-accent-blue text-white border-accent-blue'
          : 'bg-bg-input text-text-secondary border-border-base hover:border-accent-blue/40'
      }`}
    >
      {children}
    </button>
  );
}
